import React from 'react';
import styled from 'styled-components';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const ChartWrapper = styled.div`
  width: 100%;
  height: ${props => props.$height || '200px'};
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
  font-size: 13px;
`;

const LineChart = ({
  data = [],
  height = '200px',
  color = '#8b5cf6',
  dataKey = 'value',
  xKey = 'name',
  showGrid = true,
  formatValue,
  formatYAxis,
  emptyMessage = 'No data',
  // multi-series: [{ key, label, color }]
  series,
  // legacy props accepted but unused
  showDots,
  labelInterval,
}) => {
  const uid = React.useId().replace(/:/g, '');

  if (!data || data.length === 0) {
    return (
      <ChartWrapper $height={height}>
        <EmptyState>{emptyMessage}</EmptyState>
      </ChartWrapper>
    );
  }

  const isMultiSeries = Array.isArray(series) && series.length > 0;

  return (
    <ChartWrapper $height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <defs>
            {isMultiSeries
              ? series.map(s => (
                  <linearGradient key={s.key} id={`lc-grad-${uid}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor={s.color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                  </linearGradient>
                ))
              : (
                  <linearGradient id={`lc-grad-${uid}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor={color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                )
            }
          </defs>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />}
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 11, fill: '#64748b' }}
            minTickGap={20}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#64748b' }}
            width={40}
            tickFormatter={formatYAxis}
          />
          <Tooltip
            formatter={formatValue ? (v, name) => [formatValue(v), name] : undefined}
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
          />
          {isMultiSeries && (
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
          )}
          {isMultiSeries
            ? series.map(s => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  fill={`url(#lc-grad-${uid}-${s.key})`}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              ))
            : (
                <Area
                  type="monotone"
                  dataKey={dataKey}
                  stroke={color}
                  fill={`url(#lc-grad-${uid})`}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              )
          }
        </AreaChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
};

export default LineChart;
